import type { LiveEvent as ApiLiveEvent } from "../../../../api/src/application/live/live-event";
import { shouldDeliverLiveEvent } from "./live-sync";
import { trpc } from "./trpc";

export type LiveEvent = ApiLiveEvent;

type SpaceSubscription = {
  lastVersion: string | null;
  listeners: Set<(event: LiveEvent) => void>;
  reconnectTimer: number | null;
  retryAttempt: number;
  startedOnce: boolean;
  teardownTimer: number | null;
  transport: { unsubscribe(): void } | null;
};

const subscriptions = new Map<string, SpaceSubscription>();

export function subscribeToSpaceLiveEvents({
  onEvent,
  signal,
  spaceId,
}: {
  onEvent: (event: LiveEvent) => void;
  signal: AbortSignal;
  spaceId: string;
}) {
  if (signal.aborted) return;
  let shared = subscriptions.get(spaceId);
  if (shared && shared.teardownTimer !== null) {
    window.clearTimeout(shared.teardownTimer);
    shared.teardownTimer = null;
  }
  if (!shared) {
    shared = {
      lastVersion: null,
      listeners: new Set(),
      reconnectTimer: null,
      retryAttempt: 0,
      startedOnce: false,
      teardownTimer: null,
      transport: null,
    };
    subscriptions.set(spaceId, shared);
    connect(spaceId, shared);
  }

  shared.listeners.add(onEvent);
  signal.addEventListener("abort", () => {
    const current = subscriptions.get(spaceId);
    if (current !== shared) return;
    current.listeners.delete(onEvent);
    if (current.listeners.size !== 0 || current.teardownTimer !== null) return;
    current.teardownTimer = window.setTimeout(() => {
      const latest = subscriptions.get(spaceId);
      if (latest !== current || current.listeners.size !== 0) return;
      if (current.reconnectTimer !== null) window.clearTimeout(current.reconnectTimer);
      current.transport?.unsubscribe();
      subscriptions.delete(spaceId);
    }, 250);
  }, { once: true });
}

function connect(spaceId: string, shared: SpaceSubscription) {
  shared.startedOnce = false;
  shared.transport = trpc.live.space.subscribe(
    { lastEventId: shared.lastVersion, spaceId },
    {
      onData(message) {
        shared.retryAttempt = 0;
        const version = message.data.version;
        if (shared.lastVersion !== null && BigInt(version) <= BigInt(shared.lastVersion)) return;
        shared.lastVersion = version;
        if (!shouldDeliverLiveEvent(message.data)) return;
        for (const listener of shared.listeners) listener(message.data);
      },
      onError() {
        scheduleReconnect(spaceId, shared);
      },
      onStarted() {
        shared.retryAttempt = 0;
        if (!shared.startedOnce) {
          shared.startedOnce = true;
          return;
        }
        // A repeated "started" means wsLink re-ran the subscription after a
        // dropped socket, but it resends the original input without the last
        // tracked event id. Recycle the subscription with our cursor so the
        // server replays whatever was published while we were offline.
        window.setTimeout(() => {
          if (subscriptions.get(spaceId) !== shared || shared.listeners.size === 0) return;
          shared.transport?.unsubscribe();
          connect(spaceId, shared);
        }, 0);
      },
    },
  );
}

// wsLink retries dropped connections itself; this covers subscription-fatal
// errors (e.g. an expired ticket) that would otherwise end the stream for good.
function scheduleReconnect(spaceId: string, shared: SpaceSubscription) {
  if (subscriptions.get(spaceId) !== shared || shared.reconnectTimer !== null) return;
  shared.transport?.unsubscribe();
  shared.transport = null;
  const delay = Math.min(30_000, 1_000 * 2 ** shared.retryAttempt);
  shared.retryAttempt += 1;
  shared.reconnectTimer = window.setTimeout(() => {
    shared.reconnectTimer = null;
    if (subscriptions.get(spaceId) !== shared || shared.listeners.size === 0) return;
    connect(spaceId, shared);
  }, delay);
}
